import React, { useCallback, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import {
    AuthCard,
    FieldGroup,
    InputFull,
    PasswordFieldWrapper,
    PasswordToggleButton,
    RecaptchaBox,
    ActionsWrapper,
    OptionsRow,
    KeepConnectedGroup,
    ForgotPasswordLink,
    isValidMail,
} from './AuthCard';

import { InputText, PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { getSsoBase } from '../../../services/http/httpHelper';

type Props = {
    email: string;
    password: string;
    keepConnected: boolean;
    showPassword: boolean;
    isLogin: boolean;
    message: string;
    ssoLinkNotice?: string;
    onChangeEmail: (v: string) => void;
    onChangePassword: (v: string) => void;
    onToggleKeepConnected: () => void;
    onToggleShowPassword: () => void;
    onForgotPassword: () => void;
    onEnterTyped: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onSubmit: () => void;
    onRecaptchaChange: (v: string | null) => void;
};

export const LoginCard: React.FC<Props> = ({
    email,
    password,
    keepConnected,
    showPassword,
    isLogin,
    message,
    ssoLinkNotice,
    onChangeEmail,
    onChangePassword,
    onToggleKeepConnected,
    onToggleShowPassword,
    onForgotPassword,
    onEnterTyped,
    onSubmit,
    onRecaptchaChange,
}) => {
    const [isCaptchaReady, setCaptchaReady] = useState(false);
    const [captchaKey, setCaptchaKey] = useState(0);
    const resetCaptcha = useCallback(() => {
        setCaptchaReady(false);
        onRecaptchaChange(null);
        setCaptchaKey((k) => k + 1);
    }, [onRecaptchaChange]);
    const handleSubmit = useCallback(async () => {
        try {
            await onSubmit();
            resetCaptcha();
        } catch (e) {
            resetCaptcha();
            throw e;
        }
    }, [onSubmit, resetCaptcha]);

    return (
        <AuthCard
            title="Bem-vindo ao"
            subtitle="Acessar sua conta"
            message={message}
        >
            {ssoLinkNotice ? (
                <div
                    style={{
                        marginBottom: 12,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                        color: '#1E3A8A',
                        fontSize: 13,
                        lineHeight: 1.4,
                    }}
                >
                    {ssoLinkNotice}
                </div>
            ) : null}

            <FieldGroup>
                <InputFull
                    onKeyUp={onEnterTyped}
                    placeholder="E-mail"
                    value={email}
                    onChange={(e) => onChangeEmail(e.target.value)}
                />

                <PasswordFieldWrapper>
                    <InputFull
                        onKeyUp={onEnterTyped}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Senha"
                        value={password}
                        onChange={(e) => onChangePassword(e.target.value)}
                    />
                    <PasswordToggleButton
                        type="button"
                        onClick={onToggleShowPassword}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </PasswordToggleButton>
                </PasswordFieldWrapper>

                <OptionsRow>
                    <KeepConnectedGroup>
                        <InputText
                            type="checkbox"
                            style={{ width: '16px', height: '16px' }}
                            onClick={onToggleKeepConnected}
                            checked={keepConnected}
                            readOnly
                        />
                        <span>Manter conectado</span>
                    </KeepConnectedGroup>

                    <ForgotPasswordLink onClick={onForgotPassword}>
                        Esqueceu sua senha?
                    </ForgotPasswordLink>
                </OptionsRow>
            </FieldGroup>

            <RecaptchaBox
                key={captchaKey}
                isReady={isCaptchaReady}
                onChangeReady={setCaptchaReady}
                onChangePayload={onRecaptchaChange}
            />

            {isLogin ? (
                <div
                    style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}
                >
                    <Spinner />
                </div>
            ) : (
                <ActionsWrapper>
                    <PrimaryButton
                        disabled={!isCaptchaReady || !isValidMail(email) || !password}
                        onClick={handleSubmit}
                    >
                        Acessar
                    </PrimaryButton>

                    <SecondaryButton
                        onClick={() => {
                            const relay = '/main';
                            window.location.href = `${getSsoBase()}/sso/login?redirect=${encodeURIComponent(
                                relay
                            )}`;
                        }}
                    >
                        Acessar com sua conta White Martins
                    </SecondaryButton>
                </ActionsWrapper>
            )}
        </AuthCard>
    );
};
